import {useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useQuery} from '@apollo/client';
import {client} from 'index';
import {GET_ADMIN_TICKETS_MIN} from 'graphql/queries/aiHelpdesk';
import {useHasSupportAccess, useIsSuperAdmin} from 'hooks';
import {useTicketAlertsStore} from '../../zustand/ticketAlertsStore';
import {playBeep} from 'utils/playBeep';
import {isDev} from 'utils/constants';

// How often to check the queue for new tickets.
const POLL_MS = 22000;

// The queue-wide buckets we watch (the per-agent "awaiting" one is below). "Incoming" = every not-yet-resolved AI/human state;
// "escalated" is watched on its own so an AI_ACTIVE → ESCALATED transition still
// registers (escalated rises even when the combined total is flat).
const INCOMING_STATUSES = ['GUIDED_FLOW', 'AI_ACTIVE', 'HUMAN_ACTIVE'];
const ESCALATED_STATUSES = ['ESCALATED'];

// Count-only variables (page of 1, we only read pagination.total). Mirror the
// app's convention: a single status goes on `status`, multiple on `statuses` —
// the backend errors on a single-element `statuses` array.
const countVariables = statuses => {
  const input = {
    pagination: {paginate: true, page: 1, limit: 1, offset: 0},
  };
  if (statuses.length === 1) {
    input.status = statuses[0];
  } else {
    input.statuses = statuses;
  }
  return {input};
};

// Tickets assigned to the logged-in agent where the customer is waiting on a
// reply. Shared with the "Awaiting your reply" tab badge.
export const awaitingCountVariables = {
  input: {
    assignedToMe: true,
    awaitingReply: true,
    pagination: {paginate: true, page: 1, limit: 1, offset: 0},
  },
};

/**
 * Re-run every live queue count now (watcher buckets + tab badge); with
 * `lists`, the visible ticket list too, e.g. after a reply from the chat view,
 * where the list sits beside the conversation.
 */
export const refreshTicketCounts = ({lists = false} = {}) =>
  client.refetchQueries({
    include: 'active',
    onQueryUpdated: query =>
      query.queryName === 'GetAdminTicketsMin' ||
      (lists && query.queryName === 'GetAdminTickets'),
  });

// One chirp per burst: a push and the poll it triggers can both fire for the
// same message.
let lastBeepAt = 0;
const beep = () => {
  const now = Date.now();
  if (now - lastBeepAt < 3000) return;
  lastBeepAt = now;
  playBeep();
};

export const readTotal = data => {
  const node = data?.getAdminTickets;
  if (node?.__typename === 'ResponseWithHelpdeskTickets') {
    return node.pagination?.total || 0;
  }
  return null; // error / not ready — caller skips this tick for this bucket
};

/**
 * Headless, app-wide watcher. Mounted once in the dashboard layout so it runs
 * on every page. Drives the sidebar badge + beep. Renders nothing. Admins
 * without Customer Support access never fetch.
 *
 * Sound follows the "Customer reply alerts" toggle: off = silent (badges
 * still update); on = a chirp for
 *  - every customer reply on your tickets: from the push the server sends per
 *    message, with the awaiting-count poll as a fallback; on every page;
 *  - new escalations, for anyone with the toggle on (super admins and dev
 *    included);
 *  - new incoming tickets, for agents only (not super admins, not dev).
 */
export const TicketAlertsWatcher = () => {
  const hasAccess = useHasSupportAccess();
  const isSuperAdmin = useIsSuperAdmin();
  const alertsOn = useTicketAlertsStore(state => state.replyAlertsOn);
  const navigate = useNavigate();

  // Messages from public/admin-push-sw.js: a notification click routes inside
  // the SPA. A push means a customer just wrote (or a test was sent): chirp
  // now, even when the ticket was already waiting and the count can't rise,
  // and refresh the counts and the visible list so the new message shows.
  useEffect(() => {
    const sw = navigator.serviceWorker;
    if (!sw) return undefined;
    const onMessage = event => {
      if (event.data?.source !== 'admin-push') return;
      if (event.data.navigate) {
        navigate(event.data.navigate);
        return;
      }
      if (alertsOn) beep();
      refreshTicketCounts({lists: true});
    };
    sw.addEventListener('message', onMessage);
    // addEventListener (unlike onmessage) doesn't start delivery by itself.
    sw.startMessages?.();
    return () => sw.removeEventListener('message', onMessage);
  }, [navigate, alertsOn]);

  // Hook order stays stable: the inner component (which runs the queries) only
  // mounts for admins with support access, so no polling happens otherwise.
  return hasAccess ? (
    <TicketAlertsWatcherInner isSuperAdmin={isSuperAdmin} />
  ) : null;
};

const TicketAlertsWatcherInner = ({isSuperAdmin}) => {
  const location = useLocation();
  const onSupportPage = location.pathname.includes('/dashboard/ai-helpdesk');
  const observe = useTicketAlertsStore(state => state.observe);
  const clearUnseen = useTicketAlertsStore(state => state.clearUnseen);
  const alertsOn = useTicketAlertsStore(state => state.replyAlertsOn);
  // isDev: skip queue polling in dev to avoid spamming the backend. With the
  // toggle on, escalations are watched for everyone (one tiny query).
  const skipQueue = isDev || isSuperAdmin;

  const {data: incomingData} = useQuery(GET_ADMIN_TICKETS_MIN, {
    variables: countVariables(INCOMING_STATUSES),
    pollInterval: POLL_MS,
    skip: skipQueue,
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });
  const {data: escalatedData} = useQuery(GET_ADMIN_TICKETS_MIN, {
    variables: countVariables(ESCALATED_STATUSES),
    pollInterval: POLL_MS,
    skip: skipQueue && !alertsOn,
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  const {data: awaitingData} = useQuery(GET_ADMIN_TICKETS_MIN, {
    variables: awaitingCountVariables,
    pollInterval: POLL_MS,
    // One small per-agent query, so it runs on dev too while the toggle is on.
    skip: !alertsOn,
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  const incomingTotal = readTotal(incomingData);
  const escalatedTotal = readTotal(escalatedData);
  const awaitingTotal = readTotal(awaitingData);

  // Each bucket is reconciled independently so one query failing can't block
  // the other. A rise past its high-water mark → badge, and a chirp when the
  // toggle is on.
  useEffect(() => {
    if (incomingTotal === null) return;
    const delta = observe('incoming', incomingTotal, onSupportPage);
    if (delta > 0 && alertsOn) beep();
  }, [incomingTotal, onSupportPage, observe, alertsOn]);

  // Escalations and replies waiting on you chirp on every page, Customer
  // Support included: worth hearing even while you're in another ticket.
  useEffect(() => {
    if (escalatedTotal === null) return;
    const delta = observe(
      'escalated',
      escalatedTotal,
      alertsOn ? false : onSupportPage,
    );
    if (delta > 0 && alertsOn) beep();
    if (alertsOn && onSupportPage) clearUnseen();
  }, [escalatedTotal, onSupportPage, observe, alertsOn, clearUnseen]);

  useEffect(() => {
    if (!alertsOn || awaitingTotal === null) return;
    const delta = observe('awaiting', awaitingTotal, false);
    if (delta > 0) beep();
    if (onSupportPage) clearUnseen();
  }, [alertsOn, awaitingTotal, onSupportPage, observe, clearUnseen]);

  return null;
};
